import { useRef } from "react";

import { PauseIcon, PlayIcon } from "lucide-react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import ApplyingTemplateDialog from "features/Session/ApplyingTemplateDialog";
import SessionExportingDialog from "features/Session/SessionExportingDialog";
import SessionHeader from "features/Session/SessionHeader";
import UploadImageDialog from "features/Session/UploadImageDialog";
import UploadingDocumentDialog from "features/Session/UploadingDocumentDialog";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "components/Accordion";
import Button from "components/Button";
import Container from "components/Container";
import Content from "components/Content";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/DropdownMenu";
import Editor from "components/Editor";
import Footer from "components/Footer";
import { Form, FormField } from "components/Form";

import PatientForm from "../PatientForm";
import SessionStopDialog from "../SessionStopDialog";
import SessionSummarizingDialog from "../SessionSummarizingDialog";

import { useSessionSummarize } from "./useSessionSummarize";

export default function SessionSummarize() {
  const { t } = useTranslation();
  const scanUploadInputRef = useRef<HTMLInputElement>(null);
  const scanCameraInputRef = useRef<HTMLInputElement>(null);

  const {
    isSessionLoading,
    form,
    isValid,
    control,
    handleSubmit,
    onSubmit,
    handleContinueSession,
    setPauseOpen,
    pauseOpen,
    handleStopSession,
    exportOpen,
    setExportOpen,
    isSummarizing,
    isExporting,
    open,
    reportTemplates,
    applyTemplate,
    isApplyingTemplate,
    uploadScanedDocument,
    isUploadingScanedDocument,
    isUploadingScanedDocumentSuccess,
  } = useSessionSummarize();

  const handlePauseSession = () => {
    setPauseOpen(true);
  };

  const triggerScanInput = (inputRef: { current: HTMLInputElement | null }) => {
    if (!inputRef.current) return;
    inputRef.current.value = "";
    inputRef.current.click();
  };

  const handleScanFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error(t("session.uploadingDocumentDialog.scanFileTypeError"));
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("session.uploadingDocumentDialog.scanFileSizeError"));
      e.target.value = "";
      return;
    }

    uploadScanedDocument(file);
    e.target.value = "";
  };

  return (
    <>
      <SessionHeader />
      <Content isLoading={isSessionLoading}>
        <Container className="pt-16 pb-6 md:pb-24">
          <Form {...form}>
            <form
              className="flex flex-col justify-between px-4 md:px-0 mb-20 md:mb-0"
              id="session-summarize-form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Accordion type="single" collapsible>
                <AccordionItem value="item-1">
                  <AccordionTrigger>{t("session.aboutPatient")}</AccordionTrigger>
                  <AccordionContent>
                    <PatientForm />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <div className="flex flex-col gap-4 mt-4">
                <FormField
                  control={control}
                  name="report"
                  render={({ field }) => (
                    <Editor
                      content={field.value}
                      onChange={field.onChange}
                      isSummarize
                      reportTemplates={reportTemplates}
                      applyTemplate={applyTemplate}
                      children={
                        <div className="flex flex-col gap-2">
                          <input
                            ref={scanUploadInputRef}
                            id="scan-upload"
                            type="file"
                            accept="image/jpeg,image/png,image/*"
                            style={{ display: "none" }}
                            onChange={handleScanFileChange}
                            disabled={isUploadingScanedDocument}
                          />
                          <input
                            ref={scanCameraInputRef}
                            id="scan-camera"
                            type="file"
                            accept="image/*"
                            capture="environment"
                            style={{ display: "none" }}
                            onChange={handleScanFileChange}
                            disabled={isUploadingScanedDocument}
                          />

                          {isMobile ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="small"
                                  className="h-8 gap-1 px-2 hover:bg-general-gray-200"
                                  type="button"
                                  disabled={isUploadingScanedDocument}
                                >
                                  {t("session.uploadScannedDocument")}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="w-52">
                                <DropdownMenuGroup>
                                  <DropdownMenuItem onClick={() => triggerScanInput(scanUploadInputRef)}>
                                    {t("session.uploadScannedDocument")}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => triggerScanInput(scanCameraInputRef)}>
                                    {t("session.openCamera")}
                                  </DropdownMenuItem>
                                </DropdownMenuGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <Button
                              variant="ghost"
                              size="small"
                              className="h-8 gap-1 px-2 hover:bg-general-gray-200"
                              type="button"
                              onClick={() => triggerScanInput(scanUploadInputRef)}
                              disabled={isUploadingScanedDocument}
                            >
                              {t("session.uploadScannedDocument")}
                            </Button>
                          )}
                        </div>
                      }
                    />
                  )}
                />
              </div>
              <UploadImageDialog isUploadingScanedDocumentSuccess={isUploadingScanedDocumentSuccess} />

              <Footer className="md:justify-end gap-4">
                {!isMobile ? (
                  <>
                    <Button
                      type="button"
                      className="flex-1 md:flex-none md:w-60"
                      variant="secondary"
                      onClick={handleContinueSession}
                    >
                      {t("button.returnToRecording")}
                    </Button>
                    <Button
                      type="button"
                      className="flex-1 md:flex-none md:w-60"
                      variant="secondary"
                      onClick={handlePauseSession}
                    >
                      {t("button.pauseSession")}
                    </Button>
                  </>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button aria-label="More Options" className="flex-1 md:flex-none md:w-60" variant={"secondary"}>
                        {t("button.moreOptions")}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuGroup>
                        <DropdownMenuItem onClick={handleContinueSession}>
                          <PlayIcon /> {t("button.returnToRecording")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handlePauseSession}>
                          <PauseIcon /> {t("button.pauseSession")}
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                <SessionStopDialog open={pauseOpen} setOpen={setPauseOpen} stopRecording={handleStopSession} />
                <SessionExportingDialog open={exportOpen} setOpen={setExportOpen} />
                <ApplyingTemplateDialog open={isApplyingTemplate} />
                <UploadingDocumentDialog open={isUploadingScanedDocument} />
                <Button
                  type="submit"
                  className="flex-1 md:flex-none md:w-60"
                  disabled={isSummarizing || isExporting || !isValid}
                >
                  {t("button.export")}
                </Button>
              </Footer>
            </form>
          </Form>
          <SessionSummarizingDialog open={open} />
        </Container>
      </Content>
      <SessionHeader />
    </>
  );
}
